import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppState } from 'AppProvider';
import { getExperiencePrice, getSessionPrice } from 'components/helpers';
import { SkeletonLine } from 'components/theme/Skeleton/SkeletonLine';
import { STATUS_FAILURE, STATUS_IDLE, STATUS_PENDING } from 'types/app';
import { AVAILABLE_BRAND_FEATURES } from '@kouto/types';
import { currencyFormat, getCurrencyFromCode } from 'utils';
import getTotalPriceExcludingLast from 'utils/data/sessions/getTotalPriceExcludingLast';
import * as SC from '../styles';

interface ISessionPriceProps {
  selectedSession: Record<string, unknown>;
  experience?: {
    loading: boolean;
  };
  currency?: string;
  hidePrice?: boolean;
  isPrivateBooking: boolean;
}

const SessionPrice: React.FC<ISessionPriceProps> = ({
  selectedSession,
  experience,
  isPrivateBooking,
  hidePrice,
  currency,
}) => {
  const { settings } = useAppState(
    (state: Record<string, unknown>) => state.brand,
  );
  const isRequestToBookEnabled = settings?.features?.active.includes(
    AVAILABLE_BRAND_FEATURES.ENABLE_REQUEST_BOOKINGS,
  );
  const { t: translate } = useTranslation();

  const { fetchSessionStatus, sessions, fetchFirstAvailabilityStatus } =
    useAppState((state: Record<string, unknown>) => state.experience);

  const isFetchingSessionTiers =
    fetchSessionStatus === STATUS_IDLE || fetchSessionStatus === STATUS_PENDING;

  if (fetchFirstAvailabilityStatus === STATUS_FAILURE || hidePrice) {
    return null;
  }

  if (
    isFetchingSessionTiers ||
    selectedSession === null ||
    experience?.loading
  ) {
    return <SkeletonLine translucent style={{ height: '23px' }} />;
  }

  if (isRequestToBookEnabled) {
    return (
      <span>
        <div>
          {sessions.length === 1 ? translate('from ') : ''}
          {currencyFormat(getCurrencyFromCode(currency))(
            getTotalPriceExcludingLast(sessions),
          )}
          &nbsp;/&nbsp;
          {sessions.length === 1
            ? sessions[0]?.priceTiers[0]?.name
            : `${sessions.length - 1} ${translate('nights')}`}
        </div>
      </span>
    );
  }

  if (!isFetchingSessionTiers && sessions.length === 0) {
    return (
      <span>{getExperiencePrice(experience, translate, isPrivateBooking)}</span>
    );
  }

  return (
    <SC.SidebarPrice>
      <span>
        {getSessionPrice(
          selectedSession ?? sessions[0],
          experience,
          translate,
          isPrivateBooking,
        )}
      </span>
    </SC.SidebarPrice>
  );
};

export default SessionPrice;
